import { useMemo } from 'react';

import useSubscriptionGroups from '@/state/subscription-groups';
import useClassPackGroups from '@/state/class-pack-groups';
import useClassGroups from '@/state/class-groups';
import useGiftCards from '@/state/gift-cards';
import { hasPublicSubscriptions } from '@/helpers/subscription';
import { hasPublicClassPacks } from '@/helpers/class-pack';
import { hasPublicGiftCards } from '@/helpers/gift-card';
import { getAllClasses } from '@/helpers/class';

// Retries are off, so one failed request is terminal. Every offering fails open:
// a dead-end link beats hiding a section that actually exists.
export const useSubscriptionAvailability = (enabled = true) => {
  const { data, isLoading, error } = useSubscriptionGroups({
    include_subscriptions: true,
    enabled,
  });

  return {
    subscriptionGroups: data,
    hasSubscriptions: !!error || hasPublicSubscriptions(data),
    isLoading,
    error,
  };
};

export const useClassPackAvailability = (enabled = true) => {
  const { data, isLoading, error } = useClassPackGroups({
    include_class_packs: true,
    enabled,
  });

  return {
    classPackGroups: data,
    hasClassPacks: !!error || hasPublicClassPacks(data),
    isLoading,
    error,
  };
};

export const useGiftCardAvailability = () => {
  const { data, isLoading, error } = useGiftCards();

  return {
    hasGiftCards: !!error || hasPublicGiftCards(data),
    isLoading,
  };
};

export const useClassAvailability = () => {
  const { data, isLoading, error } = useClassGroups({ include_classes: true });
  const classes = useMemo(() => getAllClasses(data ?? []), [data]);

  return {
    classGroups: data,
    classes,
    hasClasses: !!error || classes.length > 0,
    isLoading,
    error,
  };
};

export default function useOfferingAvailability(enabled = true) {
  const { hasSubscriptions, isLoading: isSubscriptionsLoading } =
    useSubscriptionAvailability(enabled);
  const { hasClassPacks, isLoading: isClassPacksLoading } =
    useClassPackAvailability(enabled);

  return {
    hasSubscriptions,
    hasClassPacks,
    isLoading: isSubscriptionsLoading || isClassPacksLoading,
  };
}
